import { acceptHMRUpdate, defineStore } from 'pinia'
import type { ToastPluginApi } from 'vue-toast-notification'

import type { Notification, NotificationSocket } from '~/models/notification'
import type { ApiPaginationMeta } from '~/types/api'
import { normalizeNotificationProductId } from '~/utils/notificationProduct'

interface UnreadCountResponse {
    data: { count: number }
}

export interface LotResultPopupPayload {
    type: 'auction_won' | 'auction_lost'
    result: 'won' | 'lost'
    product_id: number
    product_name: string
    message?: string
    id?: number | string
}

export interface NotificationMeta extends ApiPaginationMeta {
    total: number
    current_page: number
    per_page: number
    last_page: number
}

interface PaginatedNotificationsResponse {
    data: Notification[]
    meta: NotificationMeta
}

export const useNotificationStore = defineStore('notifications', () => {
    const client = useSanctumClient()
    const { $toast } = useNuxtApp() as { $toast: ToastPluginApi }

    const playerUrl = useRuntimeConfig().public.playerUrl

    const unreadCount = ref<number>(0)
    const allNotifications = ref<Notification[]>([])
    const listMeta = ref<NotificationMeta | null>(null)

    const lotResultQueue = ref<LotResultPopupPayload[]>([])
    const activeLotResult = ref<LotResultPopupPayload | null>(null)

    const pending = ref<boolean>(false)
    const loadMorePending = ref<boolean>(false)
    const initialized = ref<boolean>(false)

    const hasMorePages = computed(() => {
        const m = listMeta.value
        return m ? m.current_page < m.last_page : false
    })

    const fetchUnreadCount = async () => {
        try {
            const res = await client<UnreadCountResponse>(`${playerUrl}/notifications/unread-count`)
            unreadCount.value = res.data.count
        } catch {
            unreadCount.value = 0
        }
    }

    const loadFirstPage = async () => {
        if (pending.value) return
        pending.value = true
        try {
            const res = await client<PaginatedNotificationsResponse>(`${playerUrl}/notifications/?page=1`)
            allNotifications.value = res.data
            listMeta.value = res.meta
        } catch {
            allNotifications.value = []
            listMeta.value = null
        } finally {
            pending.value = false
        }
    }

    const loadPage = async (page: number) => {
        if (pending.value) return
        const safePage = Math.max(1, Math.floor(page || 1))
        pending.value = true
        try {
            const res = await client<PaginatedNotificationsResponse>(`${playerUrl}/notifications/?page=${safePage.toString()}`)
            allNotifications.value = res.data
            listMeta.value = res.meta
        } catch {
            allNotifications.value = []
            listMeta.value = null
        } finally {
            pending.value = false
        }
    }

    const loadMore = async () => {
        const m = listMeta.value
        if (!m || m.current_page >= m.last_page || loadMorePending.value) return
        loadMorePending.value = true
        try {
            const nextPage = m.current_page + 1
            const res = await client<PaginatedNotificationsResponse>(`${playerUrl}/notifications/?page=${nextPage.toString()}`)
            const nextItems = res.data
            allNotifications.value = [...allNotifications.value, ...nextItems]
            listMeta.value = res.meta
        } finally {
            loadMorePending.value = false
        }
    }

    const initialLoad = async () => {
        if (initialized.value) return
        initialized.value = true
        await loadFirstPage()
    }

    const resetStore = () => {
        initialized.value = false
        allNotifications.value = []
        listMeta.value = null
        unreadCount.value = 0
    }

    const deleteAllNotification = async () => {
        await client(`${playerUrl}/notifications/`, { method: 'delete' })
        allNotifications.value = []
        listMeta.value = null
        unreadCount.value = 0
    }

    const readAllNotification = async () => {
        await client(`${playerUrl}/notifications/read`, { method: 'post' })
        unreadCount.value = 0
        allNotifications.value.forEach((n) => {
            n.read_at = new Date() as unknown as Date | null
        })
        await fetchUnreadCount()
    }

    const deleteNotification = async (id: number | string) => {
        await client(`${playerUrl}/notifications/${id.toString()}`, { method: 'delete' })
        const index = allNotifications.value.findIndex((payload) => id === payload.id)
        if (index > -1) {
            const wasUnread = !allNotifications.value[index].read_at
            allNotifications.value.splice(index, 1)
            if (wasUnread && unreadCount.value > 0) {
                unreadCount.value -= 1
            }
        }
    }

    const readNotification = async (id: number | string) => {
        await client(`${playerUrl}/notifications/${id.toString()}/read`, { method: 'post' })
        const item = allNotifications.value.find((payload) => id === payload.id)
        if (item && !item.read_at && unreadCount.value > 0) {
            unreadCount.value -= 1
        }
        if (item) {
            item.read_at = new Date() as unknown as Date | null
        }
    }

    /**
     * Public channel `notification` (NotifyAll) — toast only, no DB row / unread badge.
     */
    const showBroadcastAnnouncement = (payload: { message: string; type?: string }) => {
        const text = payload.message.trim()
        if (!text) return
        const toast = $toast as { success: (m: string) => void; info?: (m: string) => void }
        if (typeof toast.info === 'function') {
            toast.info(text)
        } else {
            toast.success(text)
        }
    }

    function toastForNotificationPayload(payload: NotificationSocket): void {
        const toast = $toast as {
            success: (m: string) => void
            error?: (m: string) => void
        }
        const msg = typeof payload.message === 'string' ? payload.message.trim() : ''

        if (payload.type === 'bid_placed') {
            return
        }

        if (
            payload.type === 'outbid'
            || payload.type === 'auction_lost'
            || payload.type === 'payment_failed'
        ) {
            if (typeof toast.error === 'function') {
                toast.error(msg || 'Something went wrong.')
            } else {
                toast.success(msg)
            }
            return
        }

        if (msg) {
            toast.success(msg)
        }
    }

    const addNotification = (payload: NotificationSocket) => {
        toastForNotificationPayload(payload)
        const productId = normalizeNotificationProductId(payload.product_id)

        if (
            (payload.type === 'auction_won' || payload.type === 'auction_lost') &&
            productId !== null &&
            typeof payload.product_name === 'string' &&
            (payload.result === 'won' || payload.result === 'lost')
        ) {
            const popup: LotResultPopupPayload = {
                id: payload.id,
                type: payload.type,
                result: payload.result,
                product_id: productId,
                product_name: payload.product_name,
                message: payload.message,
            }
            lotResultQueue.value.push(popup)
            if (!activeLotResult.value) {
                activeLotResult.value = lotResultQueue.value.shift() ?? null
            }
        }

        if (!payload.id) {
            void fetchUnreadCount()
            return
        }
        unreadCount.value = Math.max(0, unreadCount.value + 1)
        const newItem: Notification = {
            ...payload,
            id: payload.id,
            title: payload.title,
            product_id: productId,
            product_name: payload.product_name,
            amount: payload.amount,
            balance: payload.balance,
            reason: payload.reason,
            reason_label: payload.reason_label,
            created_at: null,
            read_at: null,
            deleted_at: null,
        }
        allNotifications.value.unshift(newItem)
    }

    const dismissLotResultPopup = () => {
        activeLotResult.value = lotResultQueue.value.shift() ?? null
    }

    return {
        methods: {
            initialLoad,
            loadFirstPage,
            loadPage,
            loadMore,
            fetchUnreadCount,
            deleteAllNotification,
            deleteNotification,
            readAllNotification,
            readNotification,
            addNotification,
            showBroadcastAnnouncement,
            dismissLotResultPopup,
            resetStore,
        },
        data: {
            allNotifications,
            listMeta,
            unreadCount,
            activeLotResult,
        },
        pending,
        loadMorePending,
        hasMorePages,
        initialized,
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(useNotificationStore, import.meta.hot)
    )
}
